import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { useGame } from '../contexts/GameContext';
import { useMatchCache } from '../contexts/MatchCacheContext';
import { usePlayerCache } from '../contexts/PlayerCacheContext';
import { useAlert } from '../contexts/AlertContext';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  getDoc,
  addDoc, 
  updateDoc, 
  doc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { isSuperAdmin } from '../constants/roles';
import { groupMatchesGame } from '../constants/groups';
import { assignTeamsToRegistrations } from '../utils/tournamentUtils';
import { sendMatchSchedule, isEmailConfigured } from '../services/emailService';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../services/auditLogService';
import { tournamentVisibleToUser as tournamentVisibleToUserShared } from '../utils/groupVisibility';
import EntityProfileFields, { EntityProfileDisplay } from '../components/EntityProfileFields';
import VenueSearch from '../components/VenueSearch';
import { ensureVenueBlockForMatch } from '../services/venueBlockService';
import { getAvailabilityForPlayers, createAvailabilityChecker } from '../services/playerAvailabilityService';
import {
  generateAllGroupMatches,
  generateAdditionalGroupMatchesForNew,
  generateFinalsSchedule,
  migrateScheduleConfig,
} from '../utils/scheduleStrategy';
import './TournamentManagement.css';

/** Registrations that count for matches: approved, or legacy registered/no status. */
function approvedForTournament(registrations, tournamentId) {
  return registrations.filter(
    reg => reg.tournamentId === tournamentId &&
      (reg.status === 'approved' || reg.status === 'registered' || reg.status == null)
  );
}

function VisibilityEditor({ visibility, groupIds, groups, apartmentIds, apartments, onSave, onCancel }) {
  const vis = visibility || 'open';
  const useGroups = vis === 'groups' || (vis !== 'apartments' && groups?.length > 0);
  const [v, setV] = React.useState(useGroups ? 'groups' : vis);
  const [groupIdsSel, setGroupIdsSel] = React.useState(Array.isArray(groupIds) ? [...groupIds] : (Array.isArray(apartmentIds) ? [...apartmentIds] : []));
  const [aptIds, setAptIds] = React.useState(Array.isArray(apartmentIds) ? [...apartmentIds] : []);
  const handleSave = () => {
    if (v === 'groups') onSave('groups', groupIdsSel);
    else if (v === 'apartments') onSave('apartments', aptIds);
    else onSave('open', []);
  };
  return (
    <div className="config-section" style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
      <label>Who can see this tournament</label>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <label className="checkbox-label">
          <input type="radio" name="vis" checked={v === 'open'} onChange={() => setV('open')} />
          Open (everyone)
        </label>
        <label className="checkbox-label">
          <input type="radio" name="vis" checked={v === 'groups'} onChange={() => setV('groups')} />
          Specific groups
        </label>
        {apartments?.length > 0 && (
          <label className="checkbox-label">
            <input type="radio" name="vis" checked={v === 'apartments'} onChange={() => setV('apartments')} />
            Specific apartments (legacy)
          </label>
        )}
      </div>
      {v === 'groups' && groups?.length > 0 && (
        <div style={{ marginBottom: '8px' }}>
          {groups.map(g => (
            <label key={g.id} className="checkbox-label" style={{ display: 'block', marginBottom: '4px' }}>
              <input
                type="checkbox"
                checked={groupIdsSel.includes(g.id)}
                onChange={(e) => {
                  if (e.target.checked) setGroupIdsSel([...groupIdsSel, g.id]);
                  else setGroupIdsSel(groupIdsSel.filter(id => id !== g.id));
                }}
              />
              {g.shortCode ? `${g.name} (${g.shortCode})` : (g.name || g.id)}
            </label>
          ))}
        </div>
      )}
      {v === 'apartments' && apartments?.length > 0 && (
        <div style={{ marginBottom: '8px' }}>
          {apartments.map(apt => (
            <label key={apt.id} className="checkbox-label" style={{ display: 'block', marginBottom: '4px' }}>
              <input
                type="checkbox"
                checked={aptIds.includes(apt.id)}
                onChange={(e) => {
                  if (e.target.checked) setAptIds([...aptIds, apt.id]);
                  else setAptIds(aptIds.filter(id => id !== apt.id));
                }}
              />
              {apt.name || apt.id}
            </label>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="button" className="btn-primary" onClick={handleSave}>Save</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function TournamentManagement() {
  const { currentUser, userProfile } = useAuth();
  const { switchRole } = useRole();
  const { gameId } = useGame();
  const { clearCache: clearMatchCache } = useMatchCache();
  const { getPlayerName } = usePlayerCache();
  const { showAlert, showConfirmPromise, showToast } = useAlert();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [matchesByTournamentId, setMatchesByTournamentId] = useState({});
  const [apartments, setApartments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [venues, setVenues] = useState([]);
  const [firestoreVenueIds, setFirestoreVenueIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingVisibilityId, setEditingVisibilityId] = useState(null);
  const [newTournament, setNewTournament] = useState({
    name: '',
    description: '',
    startDate: '',
    registrationsClosed: false,
    visibility: 'open',
    apartmentIds: [],
    groupIds: [],
    defaultVenueId: '',
    defaultCourtId: '',
    avatarURL: '',
    phone: '',
    address: '',
    mapUrl: ''
  });
  const [showScheduleConfig, setShowScheduleConfig] = useState({});
  const [showVenueConfig, setShowVenueConfig] = useState({});
  const [showRulesConfig, setShowRulesConfig] = useState({});
  const [replaceMemberTournamentId, setReplaceMemberTournamentId] = useState(null);
  const [replaceTeamId, setReplaceTeamId] = useState('');
  const [replaceOldPlayerId, setReplaceOldPlayerId] = useState('');
  const [replaceNewPlayerId, setReplaceNewPlayerId] = useState('');
  const [scheduleConfig, setScheduleConfig] = useState({
    weekdays: [],
    scheduleStart: '18:00',
    scheduleEnd: '21:00',
    matchDuration: 30, // minutes
    bufferDays: 2,
    singlesGroupSize: 4,
    doublesGroupSize: 4,
    defaultVenueId: '',
    defaultCourtId: '',
    venueIds: []
  });
  /** When true, players are asked to suggest their availability for scheduling. */
  const [askPlayerAvailability, setAskPlayerAvailability] = useState(false);
  /** When false, doubles matches do not require selecting which player scored (tournament-level). */
  const [doublesRequireIndividualScores, setDoublesRequireIndividualScores] = useState(true);
  /** Scoring rules: points to win (11|21) and serves per rotation (2|5) for singles and doubles. */
  const [scoringConfig, setScoringConfig] = useState({
    singlesPointsToWin: 21,
    doublesPointsToWin: 21,
    singlesServesPerRotation: 2,
    doublesServesPerRotation: 2
  });

  const isSuperAdminUser = isSuperAdmin(userProfile);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    if (userProfile?.isAdmin || isSuperAdminUser) switchRole('admin');
    fetchTournaments();
    fetchRegistrations();
    fetchMatches();
    fetchApartments();
    fetchGroups();
    fetchVenues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userProfile, navigate, gameId]);

  async function fetchApartments() {
    try {
      const snap = await getDocs(collection(db, 'apartments'));
      setApartments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching apartments:', err);
    }
  }

  async function fetchGroups() {
    try {
      const snap = await getDocs(collection(db, 'groups'));
      setGroups(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  }

  async function fetchVenues() {
    try {
      const snap = await getDocs(collection(db, 'venues'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setVenues(list);
      setFirestoreVenueIds(list.map(v => v.id));
    } catch (err) {
      console.error('Error fetching venues:', err);
    }
  }

  function auditContext() {
    return {
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      displayName: userProfile?.displayName || currentUser?.displayName
    };
  }

  /** Load tournament config when any config panel (venue / schedule / rules) is opened. */
  useEffect(() => {
    const scheduleTid = Object.keys(showScheduleConfig).find(id => showScheduleConfig[id]);
    const venueTid = Object.keys(showVenueConfig).find(id => showVenueConfig[id]);
    const rulesTid = Object.keys(showRulesConfig).find(id => showRulesConfig[id]);
    const tournamentId = scheduleTid || venueTid || rulesTid;
    if (tournamentId) {
      const tournament = tournaments.find(t => t.id === tournamentId);
      if (tournament) {
        const venueIds = Array.isArray(tournament.venueIds) ? tournament.venueIds : (tournament.scheduleConfig?.venueIds || (tournament.defaultVenueId ? [tournament.defaultVenueId] : []));
        if (tournament.scheduleConfig) {
          setScheduleConfig({
            ...migrateScheduleConfig(tournament.scheduleConfig),
            venueIds,
            defaultVenueId: tournament.defaultVenueId || '',
            defaultCourtId: tournament.defaultCourtId || ''
          });
        } else {
          setScheduleConfig(prev => ({
            ...prev,
            venueIds,
            defaultVenueId: tournament.defaultVenueId || '',
            defaultCourtId: tournament.defaultCourtId || ''
          }));
        }
        setAskPlayerAvailability(tournament.askPlayerAvailability === true);
        setDoublesRequireIndividualScores(tournament.doublesRequireIndividualScores !== false);
        if (tournament.scoringConfig) {
          setScoringConfig({
            singlesPointsToWin: tournament.scoringConfig.singlesPointsToWin ?? 21,
            doublesPointsToWin: tournament.scoringConfig.doublesPointsToWin ?? 21,
            singlesServesPerRotation: tournament.scoringConfig.singlesServesPerRotation ?? 2,
            doublesServesPerRotation: tournament.scoringConfig.doublesServesPerRotation ?? 2
          });
        } else {
          setScoringConfig({
            singlesPointsToWin: 21,
            doublesPointsToWin: 21,
            singlesServesPerRotation: 2,
            doublesServesPerRotation: 2
          });
        }
      }
    }
  }, [showScheduleConfig, showVenueConfig, showRulesConfig, tournaments]);

  function tournamentVisibleToUser(t) {
    return tournamentVisibleToUserShared(t, userProfile);
  }

  /** Groups that apply to the current sport (same group can be used across sports). */
  const groupsForGame = (groups || []).filter(g => groupMatchesGame(g, gameId));

  /** Creator, tournament admin (adminIds), or system admin can manage this tournament. */
  function canManageTournament(t) {
    if (!t || !currentUser?.uid) return false;
    if (isSuperAdminUser || userProfile?.isAdmin) return true;
    if ((t.createdBy || '') === currentUser.uid) return true;
    if (Array.isArray(t.adminIds) && t.adminIds.includes(currentUser.uid)) return true;
    return false;
  }

  async function fetchTournaments() {
    try {
      const snapshot = await getDocs(collection(db, 'tournaments'));
      const all = snapshot.docs.map(d => ({ id: d.id, ...d.data(), gameId: d.data().gameId || 'table-tennis' }));
      const forGame = all.filter((t) => t.gameId === gameId);
      const list = forGame.filter(t => canManageTournament(t) || t.visibility === 'open' || tournamentVisibleToUser(t));
      setTournaments(list);
    } catch (err) {
      console.error('Error fetching tournaments:', err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchRegistrations() {
    try {
      const snapshot = await getDocs(collection(db, 'registrations'));
      const registrationsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRegistrations(registrationsList);
    } catch (err) {
      console.error('Error fetching registrations:', err);
    }
  }

  async function fetchMatches() {
    try {
      const snapshot = await getDocs(collection(db, 'matches'));
      const byT = {};
      snapshot.docs.forEach(d => {
        const data = d.data();
        const tid = data.tournamentId;
        if (!tid) return;
        if (!byT[tid]) byT[tid] = [];
        byT[tid].push({ id: d.id, ...data });
      });
      setMatchesByTournamentId(byT);
    } catch (err) {
      console.error('Error fetching matches:', err);
    }
  }

  async function handleCreateTournament(e) {
    e.preventDefault();
    if (!currentUser?.uid) return;
    try {
      const payload = {
        name: newTournament.name,
        description: newTournament.description || '',
        startDate: newTournament.startDate ? new Date(newTournament.startDate) : new Date(),
        status: 'active',
        registrationsClosed: false,
        createdAt: new Date(),
        createdBy: currentUser.uid,
        adminIds: [], // creator can add tournament admins later
        visibility: newTournament.visibility || 'open',
        apartmentIds: Array.isArray(newTournament.apartmentIds) ? newTournament.apartmentIds : [],
        groupIds: Array.isArray(newTournament.groupIds) ? newTournament.groupIds : [],
        gameId: gameId || 'table-tennis',
        defaultVenueId: newTournament.defaultVenueId || null,
        defaultCourtId: newTournament.defaultCourtId || null,
        avatarURL: (newTournament.avatarURL || '').trim() || null,
        phone: (newTournament.phone || '').trim() || null,
        address: (newTournament.address || '').trim() || null,
        mapUrl: (newTournament.mapUrl || '').trim() || null
      };
      const ref = await addDoc(collection(db, 'tournaments'), payload);
      await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.TOURNAMENT, ref.id, auditContext(), { name: payload.name }, null);
      setNewTournament({ name: '', description: '', startDate: '', registrationsClosed: false, visibility: 'open', apartmentIds: [], groupIds: [], defaultVenueId: '', defaultCourtId: '', avatarURL: '', phone: '', address: '', mapUrl: '' });
      setShowCreateForm(false);
      fetchTournaments();
    } catch (err) {
      console.error('Error creating tournament:', err);
    }
  }

  async function handleCloseRegistrations(tournamentId) {
    try {
      await updateDoc(doc(db, 'tournaments', tournamentId), {
        registrationsClosed: true
      });
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.TOURNAMENT, tournamentId, auditContext(), { registrationsClosed: true }, null);
      fetchTournaments();
    } catch (err) {
      console.error('Error closing registrations:', err);
    }
  }

  // eslint-disable-next-line no-unused-vars -- reserved for future UI
  async function handleCreateDoublesTeams(tournamentId) {
    try {
      if ((matchesByTournamentId[tournamentId] || []).length > 0) {
        showAlert('Teams cannot be created after matches have started. New players can only join as replacements.', { variant: 'warning' });
        return;
      }
      const doublesRegs = approvedForTournament(registrations, tournamentId).filter(reg => reg.type === 'doubles');

      if (doublesRegs.length < 4) {
        showAlert('Need at least 4 players registered for doubles to create teams', { variant: 'warning' });
        return;
      }

      // Check if teams already exist
      const existingTeams = doublesRegs.filter(reg => reg.teamId).length;
      if (existingTeams > 0) {
        const ok = await showConfirmPromise('Teams already exist. Do you want to recreate them?', { title: 'Recreate teams?' });
        if (!ok) return;
      }

      // Create balanced teams; when odd, ignore the player whose first login is last
      const firstLoginByPlayer = await getFirstLoginByPlayerIds(doublesRegs.map(r => r.playerId));
      const { teams, excludedPlayerId } = createBalancedTeams(doublesRegs, firstLoginByPlayer);

      // Update registrations with team assignments
      await assignTeamsToRegistrations(teams, doublesRegs);

      if (excludedPlayerId) {
        const reg = doublesRegs.find(r => r.playerId === excludedPlayerId);
        await ensureSinglesRegistrationForExcluded(tournamentId, excludedPlayerId, reg);
      }

      showToast(`Created ${teams.length} balanced teams for doubles!`, { variant: 'success' });
      fetchRegistrations();
    } catch (err) {
      console.error('Error creating teams:', err);
      showAlert('Error creating teams: ' + err.message, { variant: 'error' });
    }
  }

  async function handleAllocateTeamsAndCreateMatches(tournamentId) {
    if ((matchesByTournamentId[tournamentId] || []).length > 0) {
      showAlert('Teams and schedule cannot be created after matches have started. New players can only join as replacements.', { variant: 'warning' });
      return;
    }
    const ok = await showConfirmPromise('This will allocate teams for doubles (if needed) and create all group-stage matches. This cannot be undone.\n\nContinue?', { title: 'Create teams & schedule' });
    if (!ok) return;

    try {
      const tournament = tournaments.find(t => t.id === tournamentId);
      if (!tournament) {
        showAlert('Tournament not found', { variant: 'error' });
        return;
      }

      if (!tournament.registrationsClosed) {
        showAlert('Please close registrations first before allocating teams and creating matches', { variant: 'warning' });
        return;
      }

      const tournamentRegs = approvedForTournament(registrations, tournamentId);
      if (tournamentRegs.length === 0) {
        showAlert('No registrations found for this tournament', { variant: 'warning' });
        return;
      }

      // Step 1: Allocate teams for doubles if needed
      const doublesRegs = tournamentRegs.filter(reg => reg.type === 'doubles');
      const doublesWithoutTeams = doublesRegs.filter(reg => !reg.teamId);
      
      let effectiveRegs = tournamentRegs;
      if (doublesRegs.length >= 4 && doublesWithoutTeams.length > 0) {
        const firstLoginByPlayer = await getFirstLoginByPlayerIds(doublesRegs.map(r => r.playerId));
        const { teams, excludedPlayerId } = createBalancedTeams(doublesRegs, firstLoginByPlayer);
        await assignTeamsToRegistrations(teams, doublesRegs);
        if (excludedPlayerId) {
          const reg = doublesRegs.find(r => r.playerId === excludedPlayerId);
          await ensureSinglesRegistrationForExcluded(tournamentId, excludedPlayerId, reg);
          const snap = await getDocs(query(collection(db, 'registrations'), where('tournamentId', '==', tournamentId)));
          effectiveRegs = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r =>
            r.tournamentId === tournamentId && (r.status === 'approved' || r.status === 'registered' || r.status == null)
          );
        }
        showToast(`Allocated ${teams.length} balanced teams for doubles`, { variant: 'success' });
      } else if (doublesRegs.length > 0 && doublesRegs.every(reg => reg.teamId)) {
        showToast('Teams already allocated for doubles', { variant: 'info' });
      }

      // Step 2: Check if matches already exist
      const existingMatchesQuery = query(
        collection(db, 'matches'),
        where('tournamentId', '==', tournamentId)
      );
      const existingMatchesSnapshot = await getDocs(existingMatchesQuery);
      
      if (!existingMatchesSnapshot.empty) {
        const ok = await showConfirmPromise('Matches already exist for this tournament. Do you want to create additional matches?', { title: 'Add more matches?' });
        if (!ok) return;
      }

      // Step 3: Get tournament config
      const config = tournament?.scheduleConfig || scheduleConfig;
      
      if (!config.weekdays || config.weekdays.length === 0) {
        showAlert('Please configure weekdays and when matches can be scheduled first', { variant: 'warning' });
        setShowScheduleConfig({ [tournamentId]: true });
        return;
      }

      // Step 4: Build players and teams for group-stage schedule (use effectiveRegs so new singles reg is included)
      const singlesRegs = effectiveRegs.filter(reg => reg.type === 'singles');
      const singlesPlayers = singlesRegs.length >= 2 ? singlesRegs.map(reg => reg.playerId) : [];

      const effectiveDoublesRegs = effectiveRegs.filter(reg => reg.type === 'doubles');
      let finalDoublesRegs = effectiveDoublesRegs;
      if (effectiveDoublesRegs.length >= 4) {
        const updatedSnapshot = await getDocs(
          query(collection(db, 'registrations'), where('tournamentId', '==', tournamentId))
        );
        finalDoublesRegs = updatedSnapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(reg => reg.type === 'doubles' && (reg.status === 'approved' || reg.status === 'registered' || reg.status == null));
      }
      const updatedDoublesRegs = finalDoublesRegs.filter(reg => reg.teamId);

      const teamMap = new Map();
      updatedDoublesRegs.forEach(reg => {
        if (!teamMap.has(reg.teamId)) {
          teamMap.set(reg.teamId, {
            teamId: reg.teamId,
            teamName: reg.teamName || `Team ${reg.teamId}`,
            players: []
          });
        }
        teamMap.get(reg.teamId).players.push(reg.playerId);
      });
      const teams = updatedDoublesRegs.length >= 4 ? Array.from(teamMap.values()) : [];
      const allPlayers = effectiveRegs.map(reg => reg.playerId);

      let availabilityChecker;
      if (tournament.askPlayerAvailability) {
        const availabilityByPlayer = await getAvailabilityForPlayers(tournamentId, allPlayers);
        availabilityChecker = createAvailabilityChecker(availabilityByPlayer);
      }
      const options = availabilityChecker ? { availabilityChecker } : {};
      const groupMatches = generateAllGroupMatches(singlesPlayers, teams, tournamentId, config, allPlayers, options);
      if (groupMatches.length === 0) {
        showAlert('No matches created. Need at least 2 players for singles or 4 players (2 teams) for doubles.', { variant: 'warning' });
        return;
      }

      const matchGameId = tournament.gameId || 'table-tennis';
      const venueId = tournament.defaultVenueId || null;
      const courtId = tournament.defaultCourtId || null;
      const durationMins = config.matchDuration || 30;
      for (const match of groupMatches) {
        const payload = { ...match, gameId: matchGameId, venueId, courtId };
        const ref = await addDoc(collection(db, 'matches'), payload);
        if (venueId && match.status !== 'unschedulable') {
          const blockId = await ensureVenueBlockForMatch({ id: ref.id, ...payload }, durationMins);
          if (blockId) await updateDoc(doc(db, 'matches', ref.id), { venueBlockId: blockId });
        }
      }
      await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.MATCH, tournamentId, auditContext(), { count: groupMatches.length }, { tournamentId });

      const singlesCount = groupMatches.filter(m => m.matchType === 'singles').length;
      const doublesCount = groupMatches.filter(m => m.matchType === 'doubles').length;
      const unschedulableCount = groupMatches.filter(m => m.status === 'unschedulable').length;
      const summary = [];
      if (singlesCount > 0) summary.push(`${singlesCount} singles`);
      if (doublesCount > 0) summary.push(`${doublesCount} doubles`);
      if (unschedulableCount > 0) summary.push(`${unschedulableCount} need scheduling`);

      if (await isEmailConfigured()) {
        const byPlayer = new Map();
        effectiveRegs.forEach(r => { if (r.playerEmail) byPlayer.set(r.playerId, r.playerEmail); });
        const scheduleSummary = `${groupMatches.length} match(es) created (${summary.join(', ')}). View your matches in the app.`;
        for (const email of byPlayer.values()) {
          await sendMatchSchedule(email, tournament.name, scheduleSummary).catch(() => {});
          if (byPlayer.size > 1) await new Promise(r => setTimeout(r, 1100));
        }
      }

      showToast(`Created ${groupMatches.length} group-stage matches (${summary.join(', ')}) with randomized schedule!`, { variant: 'success' });
      navigate('/schedule');
    } catch (err) {
      console.error('Error allocating teams and creating matches:', err);
      showAlert('Error: ' + err.message, { variant: 'error' });
    }
  }

  /** Returns map of playerId -> first-login time (ms). Uses users.createdAt. */
  async function getFirstLoginByPlayerIds(playerIds) {
    const ids = [...new Set(playerIds)].filter(Boolean);
    const out = {};
    await Promise.all(ids.map(async (id) => {
      try {
        const snap = await getDoc(doc(db, 'users', id));
        const d = snap.exists() ? snap.data().createdAt : null;
        const ms = d && typeof d.toMillis === 'function' ? d.toMillis() : (d ? new Date(d).getTime() : 0);
        out[id] = ms;
      } catch (_) {
        out[id] = 0;
      }
    }));
    return out;
  }

  function createBalancedTeams(registrations, firstLoginByPlayerId = {}) {
    // Sort players by skill level (beginner=1, average=2, advanced=3)
    const players = registrations.map(reg => ({
      playerId: reg.playerId,
      skillLevel: reg.skillLevel || 'average',
      skillLevelValue: reg.skillLevelValue ||
        (reg.skillLevel === 'beginner' ? 1 :
         reg.skillLevel === 'average' ? 2 : 3)
    }));

    const teams = [];
    const n = players.length;
    const teamCount = Math.floor(n / 2);
    let excludedPlayerId = null;

    // If odd number of players, ignore the one whose first login is last (most recent) among registered
    let playersToUse = players;
    if (n % 2 === 1) {
      const byFirstLogin = [...players].sort((a, b) => {
        const ta = firstLoginByPlayerId[a.playerId] ?? 0;
        const tb = firstLoginByPlayerId[b.playerId] ?? 0;
        return ta - tb;
      });
      excludedPlayerId = byFirstLogin[byFirstLogin.length - 1].playerId;
      playersToUse = byFirstLogin.slice(0, -1);
    }

    // Sort by skill level value for pairing
    playersToUse.sort((a, b) => a.skillLevelValue - b.skillLevelValue);

    // Create teams by pairing players from opposite ends of skill spectrum
    // This ensures balanced teams (best + worst, second best + second worst, etc.)
    for (let i = 0; i < teamCount; i++) {
      const player1 = playersToUse[i];
      const player2 = playersToUse[playersToUse.length - 1 - i];

      const teamId = `team-${i + 1}`;
      const teamName = `Team ${i + 1}`;
      const avgSkill = (player1.skillLevelValue + player2.skillLevelValue) / 2;

      teams.push({
        teamId,
        teamName,
        players: [player1.playerId, player2.playerId],
        averageSkill: avgSkill,
        skillLevels: [player1.skillLevel, player2.skillLevel]
      });
    }

    return { teams, excludedPlayerId };
  }

  /** If excludedPlayerId has no singles reg for this tournament, add one so they get singles matches. */
  async function ensureSinglesRegistrationForExcluded(tournamentId, excludedPlayerId, doublesRegForPlayer) {
    if (!excludedPlayerId) return;
    const hasSingles = registrations.some(
      r => r.tournamentId === tournamentId && r.playerId === excludedPlayerId && r.type === 'singles' &&
        (r.status === 'approved' || r.status === 'registered' || r.status == null)
    );
    if (hasSingles) return;
    const reg = doublesRegForPlayer || registrations.find(
      r => r.tournamentId === tournamentId && r.playerId === excludedPlayerId && r.type === 'doubles'
    );
    const regRef = await addDoc(collection(db, 'registrations'), {
      tournamentId,
      playerId: excludedPlayerId,
      playerEmail: reg?.playerEmail || null,
      type: 'singles',
      skillLevel: reg?.skillLevel || 'average',
      skillLevelValue: reg?.skillLevelValue ?? (reg?.skillLevel === 'beginner' ? 1 : reg?.skillLevel === 'advanced' ? 3 : 2),
      status: 'approved'
    });
    await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.REGISTRATION, regRef.id, auditContext(), { type: 'singles', status: 'approved' }, { tournamentId });
  }

  // eslint-disable-next-line no-unused-vars -- reserved for future UI
  async function handleGenerateSchedule(tournamentId) {
    try {
      if ((matchesByTournamentId[tournamentId] || []).length > 0) {
        showAlert('Schedule cannot be created or changed after matches have started.', { variant: 'warning' });
        return;
      }
      const tournament = tournaments.find(t => t.id === tournamentId);
      const config = tournament?.scheduleConfig || scheduleConfig;

      if (!config.weekdays || config.weekdays.length === 0) {
        showAlert('Please configure weekdays and when matches can be scheduled first', { variant: 'warning' });
        setShowScheduleConfig({ [tournamentId]: true });
        return;
      }

      const tournamentRegs = approvedForTournament(registrations, tournamentId);
      const singlesRegs = tournamentRegs.filter(reg => reg.type === 'singles');
      const doublesRegs = tournamentRegs.filter(reg => reg.type === 'doubles' && reg.teamId);

      const singlesPlayers = singlesRegs.length >= 2 ? singlesRegs.map(reg => reg.playerId) : [];
      const teamMap = new Map();
      doublesRegs.forEach(reg => {
        if (!teamMap.has(reg.teamId)) {
          teamMap.set(reg.teamId, {
            teamId: reg.teamId,
            teamName: reg.teamName || `Team ${reg.teamId}`,
            players: []
          });
        }
        teamMap.get(reg.teamId).players.push(reg.playerId);
      });
      const teams = doublesRegs.length >= 2 ? Array.from(teamMap.values()) : [];
      const allPlayers = tournamentRegs.map(reg => reg.playerId);

      const groupMatches = generateAllGroupMatches(singlesPlayers, teams, tournamentId, config, allPlayers);
      if (groupMatches.length === 0) {
        showAlert('Need at least 2 players for singles or 2 teams for doubles to generate schedule.', { variant: 'warning' });
        return;
      }

      const matchGameId = tournament.gameId || 'table-tennis';
      const venueId = tournament.defaultVenueId || null;
      const courtId = tournament.defaultCourtId || null;
      const durationMins = config.matchDuration || 30;
      for (const match of groupMatches) {
        const payload = { ...match, gameId: matchGameId, venueId, courtId };
        const ref = await addDoc(collection(db, 'matches'), payload);
        if (venueId) {
          const blockId = await ensureVenueBlockForMatch({ id: ref.id, ...payload }, durationMins);
          if (blockId) await updateDoc(doc(db, 'matches', ref.id), { venueBlockId: blockId });
        }
      }
      await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.MATCH, tournamentId, auditContext(), { count: groupMatches.length }, { tournamentId });

      if (await isEmailConfigured()) {
        const byPlayer = new Map();
        tournamentRegs.forEach(r => { if (r.playerEmail) byPlayer.set(r.playerId, r.playerEmail); });
        const scheduleSummary = `${groupMatches.length} match(es) created. View your matches in the app.`;
        for (const email of byPlayer.values()) {
          await sendMatchSchedule(email, tournament.name, scheduleSummary).catch(() => {});
          if (byPlayer.size > 1) await new Promise(r => setTimeout(r, 1100));
        }
      }

      showToast(`Schedule generated: ${groupMatches.length} group-stage matches with randomized schedule.`, { variant: 'success' });
      navigate('/schedule');
    } catch (err) {
      console.error('Error generating schedule:', err);
      showAlert('Error generating schedule: ' + err.message, { variant: 'error' });
    }
  }

  async function handleSaveVenueConfig(tournamentId) {
    try {
      const venueIds = Array.isArray(scheduleConfig.venueIds) ? scheduleConfig.venueIds : (scheduleConfig.defaultVenueId ? [scheduleConfig.defaultVenueId] : []);
      const hasVenue = scheduleConfig.defaultVenueId || venueIds.length > 0;
      if (!hasVenue) {
        showAlert('Select at least one venue so matches can be scheduled and auto-reschedule can run.', { variant: 'warning' });
        return;
      }
      await updateDoc(doc(db, 'tournaments', tournamentId), {
        defaultVenueId: scheduleConfig.defaultVenueId || null,
        defaultCourtId: scheduleConfig.defaultCourtId || null,
        venueIds: venueIds.length ? venueIds : (scheduleConfig.defaultVenueId ? [scheduleConfig.defaultVenueId] : [])
      });
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.TOURNAMENT, tournamentId, auditContext(), { venueConfig: true }, null);
      setShowVenueConfig({ [tournamentId]: false });
      showToast('Venue configuration saved!', { variant: 'success' });
      fetchTournaments();
    } catch (err) {
      console.error('Error saving venue config:', err);
      showAlert('Error saving venue: ' + err.message, { variant: 'error' });
    }
  }

  async function handleSaveScheduleConfig(tournamentId) {
    try {
      const { defaultVenueId, defaultCourtId, venueIds, ...configWithoutVenue } = scheduleConfig;
      const payload = { 
        scheduleConfig: configWithoutVenue,
        askPlayerAvailability: askPlayerAvailability
      };
      await updateDoc(doc(db, 'tournaments', tournamentId), payload);
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.TOURNAMENT, tournamentId, auditContext(), { scheduleConfig: true }, null);
      setShowScheduleConfig({ [tournamentId]: false });
      showToast('Schedule configuration saved!', { variant: 'success' });
      fetchTournaments();
    } catch (err) {
      console.error('Error saving schedule config:', err);
      showAlert('Error saving schedule: ' + err.message, { variant: 'error' });
    }
  }

  async function handleSaveRulesConfig(tournamentId) {
    try {
      if ((matchesByTournamentId[tournamentId] || []).length > 0) {
        showAlert('Rules cannot be edited after the tournament starts.', { variant: 'warning' });
        return;
      }
      await updateDoc(doc(db, 'tournaments', tournamentId), {
        scoringConfig: scoringConfig,
        doublesRequireIndividualScores: doublesRequireIndividualScores
      });
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.TOURNAMENT, tournamentId, auditContext(), { rules: true }, null);
      setShowRulesConfig({ [tournamentId]: false });
      showToast('Rules saved!', { variant: 'success' });
      fetchTournaments();
    } catch (err) {
      console.error('Error saving rules:', err);
      showAlert('Error saving rules: ' + err.message, { variant: 'error' });
    }
  }

  /** Replace a player in a doubles team (ongoing tournaments). Updates matches and registrations. */
  async function handleReplaceTeamMember(tournamentId, teamId, teamName, oldPlayerId, newPlayerId) {
    try {
      const newPlayerReg = registrations.find(r => r.tournamentId === tournamentId && r.playerId === newPlayerId);
      if (newPlayerReg?.type === 'doubles' && newPlayerReg?.teamId && newPlayerReg.teamId !== teamId) {
        showAlert('The new player is already in another team. They must leave that team first or choose a player who is not in any team.', { variant: 'warning' });
        return;
      }
      const tournamentMatches = (matchesByTournamentId[tournamentId] || []).filter(m => m.matchType === 'doubles');
      const toUpdate = tournamentMatches.filter(m => {
        const t1 = m.team1Id === teamId && (m.team1Players || []).includes(oldPlayerId);
        const t2 = m.team2Id === teamId && (m.team2Players || []).includes(oldPlayerId);
        return t1 || t2;
      });
      for (const m of toUpdate) {
        const updates = {};
        if (m.team1Id === teamId && (m.team1Players || []).includes(oldPlayerId)) {
          updates.team1Players = (m.team1Players || []).map(id => id === oldPlayerId ? newPlayerId : id);
        }
        if (m.team2Id === teamId && (m.team2Players || []).includes(oldPlayerId)) {
          updates.team2Players = (m.team2Players || []).map(id => id === oldPlayerId ? newPlayerId : id);
        }
        if (Object.keys(updates).length) await updateDoc(doc(db, 'matches', m.id), updates);
      }
      const oldReg = registrations.find(r => r.tournamentId === tournamentId && r.type === 'doubles' && r.playerId === oldPlayerId);
      if (oldReg) {
        await updateDoc(doc(db, 'registrations', oldReg.id), { teamId: null, teamName: null });
      }
      let newReg = registrations.find(r => r.tournamentId === tournamentId && r.type === 'doubles' && r.playerId === newPlayerId);
      if (newReg) {
        await updateDoc(doc(db, 'registrations', newReg.id), { teamId, teamName: teamName || teamId });
      } else {
        await addDoc(collection(db, 'registrations'), {
          tournamentId,
          playerId: newPlayerId,
          type: 'doubles',
          status: 'approved',
          teamId,
          teamName: teamName || teamId
        });
      }
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.MATCH, tournamentId, auditContext(), { replaceTeamMember: true, teamId, oldPlayerId, newPlayerId }, null);
      setReplaceMemberTournamentId(null);
      fetchRegistrations();
      fetchMatches();
      fetchTournaments();
      showToast('Team member replaced successfully.', { variant: 'success' });
    } catch (err) {
      console.error('Error replacing team member:', err);
      showAlert('Error replacing team member: ' + err.message, { variant: 'error' });
    }
  }

  function canEditTournamentVisibility(t) {
    return canManageTournament(t);
  }

  async function handleSaveVisibility(tournamentId, visibility, ids) {
    try {
      const payload = { visibility: visibility || 'open' };
      if (visibility === 'groups') payload.groupIds = Array.isArray(ids) ? ids : [];
      else if (visibility === 'apartments') payload.apartmentIds = Array.isArray(ids) ? ids : [];
      else { payload.groupIds = []; payload.apartmentIds = []; }
      await updateDoc(doc(db, 'tournaments', tournamentId), payload);
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.TOURNAMENT, tournamentId, auditContext(), { visibility, ids }, null);
      setEditingVisibilityId(null);
      fetchTournaments();
    } catch (err) {
      console.error('Error saving visibility:', err);
      showAlert('Error saving: ' + (err.message || err), { variant: 'error' });
    }
  }

  async function handleCreateMatchesForNewRegistrations(tournamentId) {
    try {
      if ((matchesByTournamentId[tournamentId] || []).length > 0) {
        showAlert('Matches cannot be added after the schedule has been created. New players can only join as replacements.', { variant: 'warning' });
        return;
      }
      const tournament = tournaments.find((t) => t.id === tournamentId);
      const config = migrateScheduleConfig(tournament?.scheduleConfig || scheduleConfig);
      if (!config.weekdays || config.weekdays.length === 0) {
        showAlert('Configure schedule (weekdays) first.', { variant: 'warning' });
        setShowScheduleConfig({ [tournamentId]: true });
        return;
      }
      const existingSnap = await getDocs(
        query(collection(db, 'matches'), where('tournamentId', '==', tournamentId))
      );
      const existingMatches = existingSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (existingMatches.length === 0) {
        showAlert('No existing matches. Use "Create teams & schedule" first.', { variant: 'warning' });
        return;
      }
      const tournamentRegs = approvedForTournament(registrations, tournamentId);
      // Consider singles and doubles separately: "has match" is per discipline
      const existingSinglesPlayerIds = new Set();
      const existingDoublesPlayerIds = new Set();
      const existingDoublesTeamIds = new Set();
      existingMatches.forEach((m) => {
        if (m.matchType === 'singles') {
          if (m.player1Id) existingSinglesPlayerIds.add(m.player1Id);
          if (m.player2Id) existingSinglesPlayerIds.add(m.player2Id);
        } else {
          if (m.team1Id) existingDoublesTeamIds.add(m.team1Id);
          if (m.team2Id) existingDoublesTeamIds.add(m.team2Id);
          (m.team1Players || []).forEach((id) => existingDoublesPlayerIds.add(id));
          (m.team2Players || []).forEach((id) => existingDoublesPlayerIds.add(id));
        }
      });
      const newSinglesPlayers = tournamentRegs
        .filter((r) => r.type === 'singles')
        .map((r) => r.playerId)
        .filter((pid) => !existingSinglesPlayerIds.has(pid));
      const doublesRegsWithTeam = tournamentRegs.filter((r) => r.type === 'doubles' && r.teamId);
      const teamMap = new Map();
      doublesRegsWithTeam.forEach((reg) => {
        if (!teamMap.has(reg.teamId)) {
          teamMap.set(reg.teamId, {
            teamId: reg.teamId,
            teamName: reg.teamName || reg.teamId,
            players: []
          });
        }
        teamMap.get(reg.teamId).players.push(reg.playerId);
      });
      // New doubles teams = teams whose members all have no doubles match yet (player-centric rule)
      const newDoublesTeams = Array.from(teamMap.values()).filter((t) => {
        if ((t.players || []).length < 2) return false;
        return (t.players || []).every((pid) => !existingDoublesPlayerIds.has(pid));
      });
      if (newSinglesPlayers.length === 0 && newDoublesTeams.length === 0) {
        showAlert('No new registrations. Every approved player/team is already in a match.', { variant: 'info' });
        return;
      }
      const allPlayers = tournamentRegs.map((r) => r.playerId);
      const startMatchNumber =
        Math.max(0, ...existingMatches.map((m) => m.matchNumber || 0)) + 1;
      const newDocs = generateAdditionalGroupMatchesForNew(
        existingMatches,
        newSinglesPlayers,
        newDoublesTeams,
        tournamentId,
        config,
        allPlayers,
        startMatchNumber
      );
      // Only create matches where at least one participant has no existing match (never duplicate for two already-matched sides)
      const newPlayerSet = new Set(newSinglesPlayers);
      const newTeamIds = new Set(newDoublesTeams.map((t) => t.teamId));
      const docsToCreate = newDocs.filter((d) => {
        if (d.matchType === 'singles') {
          return newPlayerSet.has(d.player1Id) || newPlayerSet.has(d.player2Id);
        }
        return newTeamIds.has(d.team1Id) || newTeamIds.has(d.team2Id);
      });
      if (docsToCreate.length === 0) {
        showAlert('No additional matches to create (new players/teams fit into existing groups with no new pairs).', { variant: 'info' });
        return;
      }
      const matchGameId = tournament.gameId || 'table-tennis';
      const venueId = tournament.defaultVenueId || null;
      const courtId = tournament.defaultCourtId || null;
      const durationMins = (tournament?.scheduleConfig || scheduleConfig).matchDuration || 30;
      for (const match of docsToCreate) {
        const payload = { ...match, gameId: matchGameId, venueId, courtId };
        const ref = await addDoc(collection(db, 'matches'), payload);
        if (venueId) {
          const blockId = await ensureVenueBlockForMatch({ id: ref.id, ...payload }, durationMins);
          if (blockId) await updateDoc(doc(db, 'matches', ref.id), { venueBlockId: blockId });
        }
      }
      await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.MATCH, tournamentId, auditContext(), { count: docsToCreate.length }, { tournamentId });
      if (await isEmailConfigured()) {
        const byPlayer = new Map();
        tournamentRegs.forEach((r) => {
          if (r.playerEmail) byPlayer.set(r.playerId, r.playerEmail);
        });
        const summary = `${docsToCreate.length} new match(es) added for new registrations.`;
        for (const email of byPlayer.values()) {
          await sendMatchSchedule(email, tournament.name, summary).catch(() => {});
          if (byPlayer.size > 1) await new Promise((r) => setTimeout(r, 1100));
        }
      }
      showToast(`Created ${docsToCreate.length} new match(es) for new registrations.`, { variant: 'success' });
      clearMatchCache();
      fetchTournaments();
      navigate('/schedule');
    } catch (err) {
      console.error('Error creating matches for new registrations:', err);
      showAlert('Error: ' + (err.message || err), { variant: 'error' });
    }
  }

  async function handleGenerateFinals(tournamentId) {
    try {
      const matchesSnapshot = await getDocs(
        query(collection(db, 'matches'), where('tournamentId', '==', tournamentId))
      );
      const allMatches = matchesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const completedMatches = allMatches.filter(m => m.status === 'completed');
      const existingFinals = allMatches.filter(m => m.phase === 'finals');

      if (existingFinals.length > 0) {
        showAlert('Finals matches already exist for this tournament.', { variant: 'warning' });
        return;
      }

      if (completedMatches.length === 0) {
        showAlert('No completed matches found. Complete group-stage matches first.', { variant: 'warning' });
        return;
      }

      const groupMatches = completedMatches.filter(m => m.phase === 'group');
      if (groupMatches.length === 0) {
        showAlert('No completed group-stage matches. Complete group matches first.', { variant: 'warning' });
        return;
      }

      const tournament = tournaments.find(t => t.id === tournamentId);
      const config = tournament?.scheduleConfig || scheduleConfig;
      const tournamentRegs = approvedForTournament(registrations, tournamentId);
      const allPlayers = tournamentRegs.map(reg => reg.playerId);

      const maxMatchNumber = allMatches.reduce((max, m) => Math.max(max, m.matchNumber || 0), 0);
      let nextMatchNumber = maxMatchNumber;
      let created = 0;

      // Singles: top 1 per group
      const singlesGroup = groupMatches.filter(m => m.matchType === 'singles');
      if (singlesGroup.length > 0) {
        const byGroup = {};
        for (const m of singlesGroup) {
          const g = m.groupId || 'A';
          if (!byGroup[g]) byGroup[g] = {};
          const p1 = m.player1Id;
          const p2 = m.player2Id;
          if (!byGroup[g][p1]) byGroup[g][p1] = { wins: 0, points: 0 };
          if (!byGroup[g][p2]) byGroup[g][p2] = { wins: 0, points: 0 };
          byGroup[g][p1].points += (m.scores?.game1?.player1 || 0) + (m.scores?.game2?.player1 || 0) + (m.scores?.game3?.player1 || 0);
          byGroup[g][p2].points += (m.scores?.game1?.player2 || 0) + (m.scores?.game2?.player2 || 0) + (m.scores?.game3?.player2 || 0);
          const w = m.winnerId || (m.winnerIds && m.winnerIds[0]);
          if (w === p1) byGroup[g][p1].wins++;
          else if (w === p2) byGroup[g][p2].wins++;
        }

        const singlesWinners = [];
        for (const g of Object.keys(byGroup)) {
          const sorted = Object.entries(byGroup[g])
            .sort((a, b) => b[1].wins - a[1].wins || b[1].points - a[1].points);
          if (sorted.length) singlesWinners.push(sorted[0][0]);
        }

        if (singlesWinners.length >= 2) {
          const matchGameId = tournament.gameId || 'table-tennis';
          const venueId = tournament.defaultVenueId || null;
          const courtId = tournament.defaultCourtId || null;
          const durationMins = config.matchDuration || 30;
          const docs = generateFinalsSchedule('singles', singlesWinners, tournamentId, config, allPlayers, nextMatchNumber);
          for (const match of docs) {
            const payload = { ...match, gameId: matchGameId, venueId, courtId };
            const ref = await addDoc(collection(db, 'matches'), payload);
            if (venueId) {
              const blockId = await ensureVenueBlockForMatch({ id: ref.id, ...payload }, durationMins);
              if (blockId) await updateDoc(doc(db, 'matches', ref.id), { venueBlockId: blockId });
            }
            created++;
          }
          nextMatchNumber += docs.length;
        }
      }

      // Doubles: top 1 per group
      const doublesGroup = groupMatches.filter(m => m.matchType === 'doubles');
      if (doublesGroup.length > 0) {
        const byGroup = {};
        for (const m of doublesGroup) {
          const g = m.groupId || 'A';
          if (!byGroup[g]) byGroup[g] = {};
          const t1 = { teamId: m.team1Id, teamName: m.team1Name, players: m.team1Players || [] };
          const t2 = { teamId: m.team2Id, teamName: m.team2Name, players: m.team2Players || [] };
          const k1 = m.team1Id;
          const k2 = m.team2Id;
          if (!byGroup[g][k1]) byGroup[g][k1] = { team: t1, wins: 0, points: 0 };
          if (!byGroup[g][k2]) byGroup[g][k2] = { team: t2, wins: 0, points: 0 };
          byGroup[g][k1].points += (m.scores?.game1?.player1 || 0) + (m.scores?.game2?.player1 || 0) + (m.scores?.game3?.player1 || 0);
          byGroup[g][k2].points += (m.scores?.game1?.player2 || 0) + (m.scores?.game2?.player2 || 0) + (m.scores?.game3?.player2 || 0);
          const winnerId = m.winnerId || (m.winnerIds && m.winnerIds[0]);
          if (m.team1Players && m.team1Players.includes(winnerId)) byGroup[g][k1].wins++;
          else if (m.team2Players && m.team2Players.includes(winnerId)) byGroup[g][k2].wins++;
        }

        const doublesWinners = [];
        for (const g of Object.keys(byGroup)) {
          const sorted = Object.entries(byGroup[g])
            .sort((a, b) => b[1].wins - a[1].wins || b[1].points - a[1].points);
          if (sorted.length) doublesWinners.push(sorted[0][1].team);
        }

        if (doublesWinners.length >= 2) {
          const matchGameId = tournament.gameId || 'table-tennis';
          const venueId = tournament.defaultVenueId || null;
          const courtId = tournament.defaultCourtId || null;
          const durationMins = config.matchDuration || 30;
          const docs = generateFinalsSchedule('doubles', doublesWinners, tournamentId, config, allPlayers, nextMatchNumber);
          for (const match of docs) {
            const payload = { ...match, gameId: matchGameId, venueId, courtId };
            const ref = await addDoc(collection(db, 'matches'), payload);
            if (venueId) {
              const blockId = await ensureVenueBlockForMatch({ id: ref.id, ...payload }, durationMins);
              if (blockId) await updateDoc(doc(db, 'matches', ref.id), { venueBlockId: blockId });
            }
            created++;
          }
        }
      }

      if (created > 0) {
        await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.MATCH, tournamentId, auditContext(), { count: created, phase: 'finals' }, { tournamentId });
      }
      if (created === 0) {
        showAlert('Could not create finals. Need at least 2 groups with completed matches.', { variant: 'warning' });
        return;
      }

      showToast('Finals round-robin matches generated successfully!', { variant: 'success' });
      navigate('/schedule');
    } catch (err) {
      console.error('Error generating finals:', err);
      showAlert('Error generating finals: ' + err.message, { variant: 'error' });
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="tournament-management-container">
      <div className="header">
        <h1>Tournament Management</h1>
        <button onClick={() => navigate('/dashboard')} className="btn-secondary">
          Back to Dashboard
        </button>
      </div>

      <div className="content">
        <div className="section">
          <div className="section-header">
            <h2>Tournaments</h2>
            <button 
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="btn-primary"
            >
              {showCreateForm ? 'Cancel' : 'Create Tournament'}
            </button>
          </div>

          {showCreateForm && (
            <form onSubmit={handleCreateTournament} className="create-form">
              <input
                type="text"
                placeholder="Tournament Name"
                value={newTournament.name}
                onChange={(e) => setNewTournament({ ...newTournament, name: e.target.value })}
                required
              />
              <textarea
                placeholder="Description"
                value={newTournament.description}
                onChange={(e) => setNewTournament({ ...newTournament, description: e.target.value })}
                rows={3}
              />
              <div className="config-section">
                <label>Profile &amp; location (optional)</label>
                <EntityProfileFields
                  value={{
                    avatarURL: newTournament.avatarURL || '',
                    description: '', phone: newTournament.phone || '',
                    address: newTournament.address || '',
                    mapUrl: newTournament.mapUrl || ''
                  }}
                  onChange={(key, val) => setNewTournament({ ...newTournament, [key]: val })}
                  showDescription={false}
                  avatarAlt="Tournament"
                />
              </div>
              <input
                type="date"
                value={newTournament.startDate}
                onChange={(e) => setNewTournament({ ...newTournament, startDate: e.target.value })}
              />
              <div className="config-section">
                <label>Who can see and register for this tournament</label>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                  Groups are the source for who can see and register. Create and manage groups from <strong>Groups</strong> in the sidebar.
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <label className="checkbox-label">
                    <input
                      type="radio"
                      name="visibility"
                      checked={(newTournament.visibility || 'open') === 'open'}
                      onChange={() => setNewTournament({ ...newTournament, visibility: 'open', groupIds: [], apartmentIds: [] })}
                    />
                    Open (everyone)
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="radio"
                      name="visibility"
                      checked={(newTournament.visibility || 'open') === 'groups'}
                      onChange={() => setNewTournament({ ...newTournament, visibility: 'groups' })}
                    />
                    Specific groups
                  </label>
                  {apartments.length > 0 && (
                    <label className="checkbox-label">
                      <input
                        type="radio"
                        name="visibility"
                        checked={(newTournament.visibility || 'open') === 'apartments'}
                        onChange={() => setNewTournament({ ...newTournament, visibility: 'apartments' })}
                      />
                      Specific apartments (legacy)
                    </label>
                  )}
                </div>
                {(newTournament.visibility || 'open') === 'groups' && (
                  <div style={{ marginTop: '8px' }}>
                    {groupsForGame.length === 0 ? (
                      <span style={{ color: '#666' }}>No groups for this sport yet. Create groups from <strong>Groups</strong> in the sidebar and optionally select sports for each group.</span>
                    ) : (
                      groupsForGame.map(g => (
                        <label key={g.id} className="checkbox-label" style={{ display: 'block', marginBottom: '4px' }}>
                          <input
                            type="checkbox"
                            checked={(newTournament.groupIds || []).includes(g.id)}
                            onChange={(e) => {
                              const ids = Array.isArray(newTournament.groupIds) ? newTournament.groupIds : [];
                              const next = e.target.checked ? [...ids, g.id] : ids.filter(id => id !== g.id);
                              setNewTournament({ ...newTournament, groupIds: next });
                            }}
                          />
                          {g.shortCode ? `${g.name} (${g.shortCode})` : (g.name || g.id)}
                        </label>
                      ))
                    )}
                  </div>
                )}
                {(newTournament.visibility || 'open') === 'apartments' && (
                  <div style={{ marginTop: '8px' }}>
                    {apartments.map(apt => (
                      <label key={apt.id} className="checkbox-label" style={{ display: 'block', marginBottom: '4px' }}>
                        <input
                          type="checkbox"
                          checked={(newTournament.apartmentIds || []).includes(apt.id)}
                          onChange={(e) => {
                            const ids = Array.isArray(newTournament.apartmentIds) ? newTournament.apartmentIds : [];
                            const next = e.target.checked ? [...ids, apt.id] : ids.filter(id => id !== apt.id);
                            setNewTournament({ ...newTournament, apartmentIds: next });
                          }}
                        />
                        {apt.name || apt.id}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="config-section">
                <label>Default venue for matches (optional)</label>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start', flexDirection: 'column' }}>
                  <VenueSearch
                    gameId={gameId || 'table-tennis'}
                    firestoreVenues={venues}
                    onSelect={(venue) => setNewTournament(prev => ({ ...prev, defaultVenueId: venue?.id || '', defaultCourtId: '' }))}
                    placeholder="Search venue by pincode, area, city or name..."
                  />
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <select
                      value={newTournament.defaultCourtId || ''}
                      onChange={(e) => setNewTournament({ ...newTournament, defaultCourtId: e.target.value || '' })}
                      disabled={!newTournament.defaultVenueId}
                    >
                      <option value="">— Any court —</option>
                      {(venues.find(v => v.id === newTournament.defaultVenueId)?.courts || []).map(c => (
                        <option key={c.id} value={c.id}>{c.name || c.id}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <button type="submit" className="btn-primary">Create</button>
            </form>
          )}

          <div className="tournaments-list">
            {tournaments.map(tournament => {
              const tournamentRegs = approvedForTournament(registrations, tournament.id);
              const vis = tournament.visibility || 'open';
              const gIds = Array.isArray(tournament.groupIds) ? tournament.groupIds : [];
              const aptIds = Array.isArray(tournament.apartmentIds) ? tournament.apartmentIds : [];
              const visLabel = vis === 'open' ? 'Open (everyone)' : vis === 'groups' ? `Specific groups (${gIds.length})` : `Specific apartments (${aptIds.length})`;
              const showingVisEdit = editingVisibilityId === tournament.id;
              return (
                <div key={tournament.id} className="tournament-card">
                  <h3>{tournament.name}</h3>
                  <p>{tournament.description}</p>
                  <EntityProfileDisplay entity={tournament} showDescription={false} />
                  <p><strong>Visibility:</strong> {visLabel}</p>
                  {canEditTournamentVisibility(tournament) && (
                    <div style={{ marginBottom: '8px' }}>
                      {!showingVisEdit ? (
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ fontSize: '13px' }}
                          onClick={() => setEditingVisibilityId(tournament.id)}
                        >
                          Configure visibility
                        </button>
                      ) : (
                        <VisibilityEditor
                          visibility={vis}
                          groupIds={gIds}
                          groups={groupsForGame}
                          apartmentIds={aptIds}
                          apartments={apartments}
                          onSave={(v, ids) => handleSaveVisibility(tournament.id, v, ids)}
                          onCancel={() => setEditingVisibilityId(null)}
                        />
                      )}
                    </div>
                  )}
                  <p><strong>Registrations:</strong> {tournamentRegs.length}</p>
                  <p><strong>Singles:</strong> {tournamentRegs.filter(r => r.type === 'singles').length}</p>
                  <p><strong>Doubles:</strong> {tournamentRegs.filter(r => r.type === 'doubles').length}</p>
                  <p><strong>Status:</strong> {tournament.registrationsClosed ? 'Closed' : 'Open'}</p>

                  {canManageTournament(tournament) && showVenueConfig[tournament.id] && (
                    <div className="schedule-config-form config-panel">
                      <h4>Edit venue</h4>
                      <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>Venues and default court used for scheduling and auto-reschedule.</p>
                      <div className="config-section">
                        <label>Venue(s)</label>
                        <div style={{ marginBottom: '8px' }}>
                          <VenueSearch
                            gameId={tournament.gameId || 'table-tennis'}
                            firestoreVenues={venues}
                            onSelect={(venue) => {
                              if (!venue) return;
                              const ids = Array.isArray(scheduleConfig.venueIds) ? [...scheduleConfig.venueIds] : [];
                              if (ids.includes(venue.id)) return;
                              if (!firestoreVenueIds.includes(venue.id)) {
                                showAlert('Claim this venue from the Venues page first, then add it here.', { variant: 'warning' });
                                return;
                              }
                              setScheduleConfig({ ...scheduleConfig, venueIds: [...ids, venue.id] });
                            }}
                            placeholder="Search by pincode, area, city or venue name..."
                          />
                        </div>
                        <div className="venue-ids-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                          {(scheduleConfig.venueIds || []).map((id) => {
                            const v = venues.find(x => x.id === id);
                            return (
                              <span key={id} className="venue-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 8px', background: '#e9ecef', borderRadius: '6px', fontSize: '14px' }}>
                                {v?.name || id}
                                <button type="button" onClick={() => setScheduleConfig({ ...scheduleConfig, venueIds: (scheduleConfig.venueIds || []).filter(x => x !== id) })} style={{ background: 'none', border: 'none', cursor: 'pointer' }} aria-label="Remove venue">×</button>
                              </span>
                            );
                          })}
                        </div>
                        <label>Default venue and court for new matches</label>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginTop: '6px' }}>
                          <select value={scheduleConfig.defaultVenueId || ''} onChange={(e) => setScheduleConfig({ ...scheduleConfig, defaultVenueId: e.target.value || '', defaultCourtId: '' })}>
                            <option value="">— Select default —</option>
                            {venues.filter(v => (v.gameIds || []).includes(tournament.gameId || 'table-tennis')).map(v => (
                              <option key={v.id} value={v.id}>{v.name || v.id}</option>
                            ))}
                          </select>
                          <select value={scheduleConfig.defaultCourtId || ''} onChange={(e) => setScheduleConfig({ ...scheduleConfig, defaultCourtId: e.target.value || '' })} disabled={!scheduleConfig.defaultVenueId}>
                            <option value="">— Any court —</option>
                            {(venues.find(v => v.id === scheduleConfig.defaultVenueId)?.courts || []).map(c => (
                              <option key={c.id} value={c.id}>{c.name || c.id}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="config-panel-actions">
                        <button type="button" className="btn-primary" onClick={() => handleSaveVenueConfig(tournament.id)}>Save</button>
                        <button type="button" className="btn-secondary" onClick={() => setShowVenueConfig({ [tournament.id]: false })}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {canManageTournament(tournament) && showScheduleConfig[tournament.id] && (
                    <div className="schedule-config-form config-panel">
                      <h4>Edit schedule</h4>
                      <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>Weekdays, time window, match duration and group sizes for generating matches.</p>
                      <div className="config-section">
                        <label>Available Weekdays</label>
                        <div className="weekday-options">
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => (
                            <label key={day} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={scheduleConfig.weekdays.includes(index + 1)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setScheduleConfig({
                                      ...scheduleConfig,
                                      weekdays: [...scheduleConfig.weekdays, index + 1].sort()
                                    });
                                  } else {
                                    setScheduleConfig({
                                      ...scheduleConfig,
                                      weekdays: scheduleConfig.weekdays.filter(w => w !== index + 1)
                                    });
                                  }
                                }}
                              />
                              {day}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="config-section">
                        <label>When matches can be scheduled (one table; one match at a time)</label>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span>
                            <label className="config-inline-label">From</label>
                            <input
                              type="time"
                              value={scheduleConfig.scheduleStart || '18:00'}
                              onChange={(e) => setScheduleConfig({ ...scheduleConfig, scheduleStart: e.target.value || '18:00' })}
                            />
                          </span>
                          <span>
                            <label className="config-inline-label">Until</label>
                            <input
                              type="time"
                              value={scheduleConfig.scheduleEnd || '21:00'}
                              onChange={(e) => setScheduleConfig({ ...scheduleConfig, scheduleEnd: e.target.value || '21:00' })}
                            />
                          </span>
                        </div>
                        <small>Matches are placed sequentially within this window (every Match Duration minutes).</small>
                      </div>
                      <div className="config-section">
                        <label>Match Duration (minutes):</label>
                        <input
                          type="number"
                          value={scheduleConfig.matchDuration}
                          onChange={(e) => setScheduleConfig({ 
                            ...scheduleConfig, 
                            matchDuration: parseInt(e.target.value) || 30 
                          })}
                          min="15"
                          max="120"
                        />
                      </div>
                      <div className="config-section">
                        <label>Buffer Days for Reschedules:</label>
                        <input
                          type="number"
                          value={scheduleConfig.bufferDays}
                          onChange={(e) => setScheduleConfig({ 
                            ...scheduleConfig, 
                            bufferDays: parseInt(e.target.value) || 2 
                          })}
                          min="1"
                          max="7"
                        />
                      </div>
                      <div className="config-section">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={askPlayerAvailability}
                            onChange={(e) => setAskPlayerAvailability(e.target.checked)}
                          />
                          Ask players to suggest availability for scheduling
                        </label>
                        <small>When enabled, registered players can set and edit their availability per tournament. Admins can reschedule unschedulable matches by availability.</small>
                      </div>
                      <div className="config-section">
                        <label>Group sizes (before creating schedules)</label>
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span>
                            <label className="config-inline-label">Players per group (singles)</label>
                            <input
                              type="number"
                              value={scheduleConfig.singlesGroupSize ?? 4}
                              onChange={(e) => setScheduleConfig({ 
                                ...scheduleConfig, 
                                singlesGroupSize: Math.max(2, parseInt(e.target.value, 10) || 4) 
                              })}
                              min="2"
                              max="12"
                            />
                          </span>
                          <span>
                            <label className="config-inline-label">Teams per group (doubles)</label>
                            <input
                              type="number"
                              value={scheduleConfig.doublesGroupSize ?? 4}
                              onChange={(e) => setScheduleConfig({ 
                                ...scheduleConfig, 
                                doublesGroupSize: Math.max(2, parseInt(e.target.value, 10) || 4) 
                              })}
                              min="2"
                              max="12"
                            />
                          </span>
                        </div>
                        <small>Target size per group. Groups are formed before round-robin. Top 1 per group advances to finals.</small>
                      </div>
                      <div className="config-panel-actions">
                        <button type="button" className="btn-primary" onClick={() => handleSaveScheduleConfig(tournament.id)}>Save</button>
                        <button type="button" className="btn-secondary" onClick={() => setShowScheduleConfig({ [tournament.id]: false })}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {canManageTournament(tournament) && showRulesConfig[tournament.id] && (() => {
                    const rulesReadOnly = (matchesByTournamentId[tournament.id] || []).length > 0;
                    return (
                      <div className="schedule-config-form config-panel rules-panel">
                        <h4>Rule engine</h4>
                        <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>Scoring and match rules for this tournament, based on sport/game type.</p>
                        {rulesReadOnly && (
                          <p style={{ fontSize: '13px', color: '#b45309', marginBottom: '8px', fontWeight: 500 }}>Rules cannot be edited after the tournament starts. View only.</p>
                        )}
                        <div className="config-section">
                          <label>Scoring rules</label>
                          <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>Points to win per game and serves per rotation (singles and doubles).</p>
                          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <span>
                              <label className="config-inline-label">Singles: points to win</label>
                              <select value={scoringConfig.singlesPointsToWin ?? 21} onChange={(e) => setScoringConfig({ ...scoringConfig, singlesPointsToWin: parseInt(e.target.value, 10) })} disabled={rulesReadOnly}>
                                <option value={11}>11</option>
                                <option value={21}>21</option>
                              </select>
                            </span>
                            <span>
                              <label className="config-inline-label">Doubles: points to win</label>
                              <select value={scoringConfig.doublesPointsToWin ?? 21} onChange={(e) => setScoringConfig({ ...scoringConfig, doublesPointsToWin: parseInt(e.target.value, 10) })} disabled={rulesReadOnly}>
                                <option value={11}>11</option>
                                <option value={21}>21</option>
                              </select>
                            </span>
                            <span>
                              <label className="config-inline-label">Singles: serves per rotation</label>
                              <select value={scoringConfig.singlesServesPerRotation ?? 2} onChange={(e) => setScoringConfig({ ...scoringConfig, singlesServesPerRotation: parseInt(e.target.value, 10) })} disabled={rulesReadOnly}>
                                <option value={2}>2</option>
                                <option value={5}>5</option>
                              </select>
                            </span>
                            <span>
                              <label className="config-inline-label">Doubles: serves per rotation</label>
                              <select value={scoringConfig.doublesServesPerRotation ?? 2} onChange={(e) => setScoringConfig({ ...scoringConfig, doublesServesPerRotation: parseInt(e.target.value, 10) })} disabled={rulesReadOnly}>
                                <option value={2}>2</option>
                                <option value={5}>5</option>
                              </select>
                            </span>
                          </div>
                          <small>Deuce (switch serve every point) at 10-10 for 11-point games, 20-20 for 21-point games.</small>
                        </div>
                        <div className="config-section">
                          <label className="checkbox-label">
                            <input type="checkbox" checked={doublesRequireIndividualScores} onChange={(e) => setDoublesRequireIndividualScores(e.target.checked)} disabled={rulesReadOnly} />
                            Require individual player scores in doubles (referee must select which player scored each point)
                          </label>
                          <small>If unchecked, doubles matches only record team scores; individual player scores are optional.</small>
                        </div>
                        <div className="config-panel-actions">
                          {!rulesReadOnly && <button type="button" className="btn-primary" onClick={() => handleSaveRulesConfig(tournament.id)}>Save</button>}
                          <button type="button" className="btn-secondary" onClick={() => setShowRulesConfig({ [tournament.id]: false })}>{rulesReadOnly ? 'Close' : 'Cancel'}</button>
                        </div>
                      </div>
                    );
                  })()}

                  {canManageTournament(tournament) && replaceMemberTournamentId === tournament.id && (() => {
                    const tournamentMatches = matchesByTournamentId[tournament.id] || [];
                    const hasMatches = tournamentMatches.length > 0;
                    const doublesRegsWithTeam = tournamentRegs.filter(r => r.type === 'doubles' && r.teamId);
                    const teamMap = new Map();
                    doublesRegsWithTeam.forEach(reg => {
                      if (!teamMap.has(reg.teamId)) teamMap.set(reg.teamId, { teamId: reg.teamId, teamName: reg.teamName || reg.teamId, players: [] });
                      teamMap.get(reg.teamId).players.push(reg.playerId);
                    });
                    const teams = Array.from(teamMap.values()).filter(t => (t.players || []).length >= 2);
                    const selectedTeam = teams.find(t => t.teamId === replaceTeamId);
                    const regPlayerIds = [...new Set(tournamentRegs.map(r => r.playerId))];
                    const playersInOtherTeams = new Set(
                      tournamentRegs.filter(r => r.type === 'doubles' && r.teamId && r.teamId !== replaceTeamId).map(r => r.playerId)
                    );
                    return (
                      <div className="schedule-config-form config-panel replace-member-panel">
                        <h4>Replace team member</h4>
                        <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>For ongoing tournaments: swap a player in a doubles team. All matches for that team will be updated.</p>
                        {!hasMatches ? (
                          <p style={{ color: '#666' }}>No matches yet. Use this after matches have been created.</p>
                        ) : teams.length === 0 ? (
                          <p style={{ color: '#666' }}>No doubles teams with two players.</p>
                        ) : (
                          <>
                            <div className="config-section">
                              <label>Team</label>
                              <select value={replaceTeamId} onChange={(e) => { setReplaceTeamId(e.target.value); setReplaceOldPlayerId(''); setReplaceNewPlayerId(''); }} style={{ width: '100%', maxWidth: '280px' }}>
                                <option value="">— Select team —</option>
                                {teams.map(t => (
                                  <option key={t.teamId} value={t.teamId}>{t.teamName || t.teamId} ({t.players.map(p => getPlayerName(p)).join(', ')})</option>
                                ))}
                              </select>
                            </div>
                            {selectedTeam && (
                              <>
                                <div className="config-section">
                                  <label>Player to replace</label>
                                  <select value={replaceOldPlayerId} onChange={(e) => setReplaceOldPlayerId(e.target.value)} style={{ width: '100%', maxWidth: '280px' }}>
                                    <option value="">— Select —</option>
                                    {(selectedTeam.players || []).map(pid => (
                                      <option key={pid} value={pid}>{getPlayerName(pid)}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="config-section">
                                  <label>New player (must be registered; cannot already be in another team)</label>
                                  <select value={replaceNewPlayerId} onChange={(e) => setReplaceNewPlayerId(e.target.value)} style={{ width: '100%', maxWidth: '280px' }}>
                                    <option value="">— Select —</option>
                                    {regPlayerIds.filter(pid => pid !== replaceOldPlayerId && !(selectedTeam.players || []).includes(pid) && !playersInOtherTeams.has(pid)).map(pid => (
                                      <option key={pid} value={pid}>{getPlayerName(pid)}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="config-panel-actions">
                                  <button type="button" className="btn-primary" disabled={!replaceOldPlayerId || !replaceNewPlayerId} onClick={() => handleReplaceTeamMember(tournament.id, selectedTeam.teamId, selectedTeam.teamName, replaceOldPlayerId, replaceNewPlayerId)}>Replace</button>
                                  <button type="button" className="btn-secondary" onClick={() => { setReplaceMemberTournamentId(null); setReplaceTeamId(''); setReplaceOldPlayerId(''); setReplaceNewPlayerId(''); }}>Cancel</button>
                                </div>
                              </>
                            )}
                          </>
                        )}
                        {hasMatches && teams.length > 0 && !selectedTeam && (
                          <div className="config-panel-actions">
                            <button type="button" className="btn-secondary" onClick={() => { setReplaceMemberTournamentId(null); setReplaceTeamId(''); setReplaceOldPlayerId(''); setReplaceNewPlayerId(''); }}>Cancel</button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div className="tournament-actions">
                    {canManageTournament(tournament) && (
                      <>
                        <div className="tournament-actions-section">
                          <span className="tournament-actions-label">Configuration</span>
                          <div className="tournament-actions-buttons">
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => { const next = !showVenueConfig[tournament.id]; setShowVenueConfig({ [tournament.id]: next }); if (next) { setShowScheduleConfig({ ...showScheduleConfig, [tournament.id]: false }); setShowRulesConfig({ ...showRulesConfig, [tournament.id]: false }); } }}
                            >
                              {showVenueConfig[tournament.id] ? 'Hide' : 'Edit venue'}
                            </button>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => { const next = !showScheduleConfig[tournament.id]; setShowScheduleConfig({ ...showScheduleConfig, [tournament.id]: next }); if (next) { setShowVenueConfig({ ...showVenueConfig, [tournament.id]: false }); setShowRulesConfig({ ...showRulesConfig, [tournament.id]: false }); } }}
                            >
                              {showScheduleConfig[tournament.id] ? 'Hide' : 'Edit schedule'}
                            </button>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => { const next = !showRulesConfig[tournament.id]; setShowRulesConfig({ [tournament.id]: next }); if (next) { setShowVenueConfig({ ...showVenueConfig, [tournament.id]: false }); setShowScheduleConfig({ ...showScheduleConfig, [tournament.id]: false }); } }}
                            >
                              {showRulesConfig[tournament.id] ? 'Hide' : 'Rule engine'}
                            </button>
                          </div>
                        </div>

                        {(matchesByTournamentId[tournament.id] || []).length > 0 && (
                          <div className="tournament-actions-section">
                            <span className="tournament-actions-label">Ongoing tournament</span>
                            <p className="tournament-actions-hint">Teams and schedule cannot be changed. New players join only as replacements.</p>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => setReplaceMemberTournamentId(replaceMemberTournamentId === tournament.id ? null : tournament.id)}
                            >
                              {replaceMemberTournamentId === tournament.id ? 'Cancel' : 'Replace team member'}
                            </button>
                            <button type="button" className="btn-success" style={{ marginTop: '8px' }} onClick={() => handleGenerateFinals(tournament.id)}>Generate finals</button>
                          </div>
                        )}

                        {!tournament.registrationsClosed && (
                          <div className="tournament-actions-section">
                            <button type="button" className="btn-warning" onClick={() => handleCloseRegistrations(tournament.id)}>Close registrations</button>
                          </div>
                        )}

                        {tournament.registrationsClosed && (matchesByTournamentId[tournament.id] || []).length === 0 && (
                          <div className="tournament-actions-section">
                            <span className="tournament-actions-label">Before start – create teams & schedule</span>
                            <p className="tournament-actions-hint">Once created, teams and schedule cannot be changed. New players can only join as replacements after that.</p>
                            <div className="allocate-teams-callout">
                              <button type="button" onClick={() => handleAllocateTeamsAndCreateMatches(tournament.id)} className="btn-allocate-teams">Create teams & schedule</button>
                            </div>
                            <button type="button" className="btn-secondary" style={{ marginTop: '8px' }} onClick={() => handleCreateMatchesForNewRegistrations(tournament.id)}>Add matches for new registrations</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

